import { calculateEnergyKcal } from "@/lib/calculateEnergyKcal";
import { calculatePAC } from "@/lib/calculatePAC";
import { calculatePOD } from "@/lib/calculatePOD";
import { clamp, roundToNearest } from "@/lib/utils";
import type { IngredientDraft } from "./preparation";
import { roundIngredientFraction as roundFraction } from "./units";
type ChocolateComposition = Pick<
  IngredientDraft,
  | "fatPct"
  | "saturatedFatPct"
  | "waterPct"
  | "proteinPct"
  | "carbPct"
  | "fiberPct"
  | "alcoholPct"
  | "sugarsPct"
  | "cocoaSolidsNonfatPct"
  | "cocoaFatPct"
  | "energyKcal"
  | "pod"
  | "pac"
>;
// These values were derived from the USDA page for cocoa powder [1]
// I assumed that all protein/carbs/fiber were from cocoa solids, so
// normalized them to the cocoa solid content (83.3%)
// [1]: https://fdc.nal.usda.gov/food-details/169593/nutrients
const COCOA_SOLIDS_NOTFAT_PROTEIN_CONTENT = 0.235;
const COCOA_SOLIDS_NOTFAT_CARB_CONTENT = 0.67;
const COCOA_SOLIDS_NOTFAT_FIBER_CONTENT = 0.44;
export function calculateChocolateComposition(
  cocoaPct: number,
  cocoaFatPct: number,
  sugarsPct: number,
): ChocolateComposition {
  const cocoaSolidsPct = clamp(cocoaPct - cocoaFatPct, 0, 1);

  const proteinPct = clamp(
    roundFraction(COCOA_SOLIDS_NOTFAT_PROTEIN_CONTENT * cocoaSolidsPct, 0.1),
    0,
    1,
  );
  const carbPct = clamp(
    roundFraction(
      COCOA_SOLIDS_NOTFAT_CARB_CONTENT * cocoaSolidsPct + sugarsPct,
      0.1,
    ),
    0,
    1,
  );
  const fiberPct = clamp(
    roundFraction(COCOA_SOLIDS_NOTFAT_FIBER_CONTENT * cocoaSolidsPct, 0.1),
    0,
    1,
  );

  const energyKcal = Math.max(
    roundToNearest(
      calculateEnergyKcal({
        fatPct: cocoaFatPct,
        proteinPct: proteinPct,
        sugarsPct: sugarsPct,
      }),
      0.1,
    ),
    0,
  );

  const pod = clamp(
    roundFraction(
      calculatePOD({
        sucrose: sugarsPct,
      }),
      0.1,
    ),
    0,
    1,
  );

  const pac = clamp(
    roundFraction(
      calculatePAC({
        sucrose: sugarsPct,
      }),
      0.1,
    ),
    0,
    1,
  );

  return {
    fatPct: cocoaFatPct,
    saturatedFatPct: 0.6 * cocoaFatPct,
    waterPct: 0,
    proteinPct,
    carbPct,
    fiberPct,
    alcoholPct: 0,
    sugarsPct: sugarsPct,
    cocoaSolidsNonfatPct: cocoaSolidsPct,
    cocoaFatPct: cocoaFatPct,
    energyKcal,
    pod,
    pac,
  };
}
